import { useMemo } from "react";
import * as Yup from "yup";
import {
  MediaDetailConfig,
  MediaDetailFeatureMapKey,
} from "@/utils/utilFunctions/Mapper/MediaDetailConfig";
import { ProductModel } from "@/database";
import {
  calculateTagCount,
  TAGS_TOTAL_CHARACTER_LIMIT,
  TAGS_TOTAL_LENGTH_ERROR_MESSAGE,
} from "@/utils/mediautils/CommonFunctions";
import {
  isFieldMandatoryLoose,
  isFieldMandatoryStrict,
  shouldShowField,
} from "@/utils/mediautils/policyHelpers";

interface UseMediaDetailsFormValidationProps {
  assetType: string;
  mediaRatio: string;
  selectedProduct: ProductModel | null;
  getMediaState: () => number | string | undefined;
  isContentType2?: boolean;
  isDefaultHost?: boolean;
  isDenmarkAccessMissing?: boolean;
}

/**
 * Custom hook to generate validation schema for MediaDetailsForm
 * Extracted from MediaDetailsForm to improve performance and reusability
 *
 * The schema function is memoized and only regenerates when dependencies change
 * The actual validation uses current formik values dynamically via getMediaState callback
 */
export const useMediaDetailsFormValidation = ({
  assetType,
  mediaRatio,
  selectedProduct,
  getMediaState,
  isContentType2 = false,
  isDefaultHost = false,
  isDenmarkAccessMissing = true,
}: UseMediaDetailsFormValidationProps) => {
  const mediaDetailConfig = useMemo(
    () => MediaDetailConfig(assetType as MediaDetailFeatureMapKey),
    [assetType]
  );

  const validationSchema = useMemo(() => {
    return () => {
      // Get media_state dynamically via callback (accesses formik via ref)
      const currentMediaState = getMediaState();
      const isActive =
        currentMediaState === 1 || String(currentMediaState) === "1";

      const requiredStringArray = (minMsg: string, requiredMsg: string) =>
        Yup.array().of(Yup.string()).min(1, minMsg).required(requiredMsg);
      const optionalStringArray = () => Yup.array().of(Yup.string());

      if (isContentType2) {
        return Yup.object().shape({
          title: Yup.string()
            .trim()
            .min(3, "Title should be equal or more than 3 characters")
            .max(100, "Title should be less than 100 characters")
            .required("Title is required"),
          landscapeThumbId:
            mediaRatio === "landscape"
              ? Yup.string().required("This field is required")
              : Yup.string(),
          portraitThumbId:
            mediaRatio === "portrait"
              ? Yup.string().required("This field is required")
              : Yup.string(),
          premiereThumbId:
            mediaRatio === "premiere"
              ? Yup.string().required("This field is required")
              : Yup.string(),
          podcastThumbId:
            mediaRatio === "podcast"
              ? Yup.string().required("This field is required")
              : Yup.string(),
        });
      }

      return Yup.object().shape({
        title:
          isActive && mediaDetailConfig.title.required
            ? Yup.string()
                .trim()
                .min(3, "Title should be equal or more than 3 characters")
                .max(100, "Title should be less than 100 characters")
                .required("Title is required")
            : Yup.string()
                .trim()
                .min(3, "Title should be equal or more than 3 characters")
                .max(100, "Title should be less than 100 characters")
                .required("Title is required"),
        title_url:
          isActive &&
          isFieldMandatoryLoose(selectedProduct?.title_for_url_policy)
            ? Yup.string().trim().required("This field is required")
            : Yup.string().trim(),
        title_url_edited:
          isActive &&
          isFieldMandatoryLoose(selectedProduct?.title_for_url_policy)
            ? Yup.string().trim().required("Title for URL is required")
            : Yup.string().trim(),
        desc:
          isActive && isFieldMandatoryLoose(selectedProduct?.desc_policy)
            ? Yup.string()
                .trim()
                .min(5, "Description should be equal or more than 5 characters")
                .max(5000, "Description should be less than 5000 characters")
                .required("Description is required")
            : Yup.string()
                .trim()
                .max(5000, "Description should be less than 5000 characters"),
        tags: (() => {
          let tagsSchema;
          const minTagsLen = !isDenmarkAccessMissing && isDefaultHost ? 5 : 1;
          const text =
            !isDenmarkAccessMissing && isDefaultHost
              ? "Atleast 5 tags required"
              : "Atleast 1 tag is required";
          if (!isActive) {
            tagsSchema = Yup.array()
              .of(Yup.string())
              .min(0, "At least one tag is required");
          } else if (
            mediaDetailConfig.tags.required &&
            isFieldMandatoryLoose(selectedProduct?.tags_policy)
          ) {
            tagsSchema = Yup.array()
              .of(Yup.string())
              .min(minTagsLen, text)
              .required("Tags are required");
          } else {
            tagsSchema = Yup.array().of(Yup.string()).min(minTagsLen, text);
          }

          const tagsFieldVisible =
            mediaDetailConfig.tags.enabled &&
            shouldShowField(selectedProduct?.tags_policy);

          if (tagsFieldVisible) {
            tagsSchema = tagsSchema.test(
              "tags-total-chars",
              TAGS_TOTAL_LENGTH_ERROR_MESSAGE,
              (value) => {
                const names = Array.isArray(value)
                  ? (value as unknown as string[])
                  : [];
                const { tagCount } = calculateTagCount(names);
                return tagCount <= TAGS_TOTAL_CHARACTER_LIMIT;
              }
            );
          }

          return tagsSchema;
        })(),
        team:
          mediaDetailConfig.team.required &&
          isFieldMandatoryLoose(selectedProduct?.team_policy)
            ? Yup.string().required("This field is required")
            : Yup.string(),
        ctype:
          isActive &&
          mediaDetailConfig.ctype.required &&
          isFieldMandatoryLoose(selectedProduct?.ctype_policy)
            ? Yup.string().required("This field is required")
            : Yup.string(),
        agency:
          isActive &&
          mediaDetailConfig.agency.required &&
          isFieldMandatoryLoose(selectedProduct?.agency_policy)
            ? Yup.string().required("This field is required")
            : Yup.string(),
        category:
          isActive &&
          mediaDetailConfig.category.required &&
          isFieldMandatoryLoose(selectedProduct?.category_policy)
            ? Yup.string().required("This field is required")
            : Yup.string(),
        lang:
          isActive && isFieldMandatoryStrict(selectedProduct?.language_policy)
            ? Yup.string().required("This field is required")
            : Yup.string(),
        subcategory:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.sub_category_policy)
            ? Yup.string().required("This field is required")
            : Yup.string(),
        asset_type:
          isActive && isFieldMandatoryStrict(selectedProduct?.asset_type_policy)
            ? Yup.string().required("This field is required")
            : Yup.string(),
        category_tags:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.categorytags_policy)
            ? Yup.array()
                .of(Yup.string())
                .min(1, "At least one category tag is required")
                .required("Category tags are required")
            : Yup.array().of(Yup.string()),
        expiry:
          isActive && isFieldMandatoryStrict(selectedProduct?.expiry_policy)
            ? Yup.number()
                .nullable()
                .required("This field is required")
                .min(1, "This field is required")
            : Yup.number().nullable(),
        is_short:
          isActive && mediaDetailConfig.is_short.required
            ? Yup.number().required("This field is required")
            : Yup.number(),
        is_campaign:
          isActive && mediaDetailConfig.is_campaign.required
            ? Yup.number().required("This field is required")
            : Yup.number(),
        youtube_id:
          isActive && isFieldMandatoryStrict(selectedProduct?.youtube_id_policy)
            ? Yup.string().required("This field is required")
            : Yup.string(),
        parentid:
          isActive && isFieldMandatoryStrict(selectedProduct?.parentid_policy)
            ? Yup.string().required("This field is required")
            : Yup.string(),
        production_type:
          isActive &&
          isFieldMandatoryLoose(selectedProduct?.production_type_policy)
            ? Yup.string().required("This field is required")
            : Yup.string(),
        mfk:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.content_rating_policy)
            ? Yup.number().required("This field is required")
            : Yup.number(),
        agency_credits:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.agency_credit_policy)
            ? requiredStringArray(
                "At least one agency credit is required",
                "Agency credits are required"
              )
            : optionalStringArray(),
        audiocredit:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.credits_policy?.audiocredit)
            ? requiredStringArray(
                "At least one audio credit is required",
                "Audio credits are required"
              )
            : optionalStringArray(),
        imagecredit:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.credits_policy?.imagecredit)
            ? requiredStringArray(
                "At least one image credit is required",
                "Image credits are required"
              )
            : optionalStringArray(),
        videocredit:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.credits_policy?.videocredit)
            ? requiredStringArray(
                "At least one video credit is required",
                "Video credits are required"
              )
            : optionalStringArray(),
        editor:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.contributor_policy?.editor)
            ? requiredStringArray(
                "At least one editor is required",
                "Editors are required"
              )
            : optionalStringArray(),
        script_writer:
          isActive &&
          isFieldMandatoryStrict(
            selectedProduct?.contributor_policy?.script_writer
          )
            ? requiredStringArray(
                "At least one script writer is required",
                "Script writers are required"
              )
            : optionalStringArray(),
        anchor:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.contributor_policy?.anchor)
            ? requiredStringArray(
                "At least one anchor is required",
                "Anchors are required"
              )
            : optionalStringArray(),
        cameraman:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.contributor_policy?.cameraman)
            ? requiredStringArray(
                "At least one cameraman is required",
                "Cameramen are required"
              )
            : optionalStringArray(),
        producer:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.contributor_policy?.producer)
            ? requiredStringArray(
                "At least one producer is required",
                "Producers are required"
              )
            : optionalStringArray(),
        photographer:
          isActive &&
          isFieldMandatoryStrict(
            selectedProduct?.contributor_policy?.photographer
          )
            ? requiredStringArray(
                "At least one photographer is required",
                "Photographers are required"
              )
            : optionalStringArray(),
        researcher:
          isActive &&
          isFieldMandatoryStrict(
            selectedProduct?.contributor_policy?.researcher
          )
            ? requiredStringArray(
                "At least one researcher is required",
                "Researchers are required"
              )
            : optionalStringArray(),
        compiler:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.contributor_policy?.compiler)
            ? requiredStringArray(
                "At least one compiler is required",
                "Compilers are required"
              )
            : optionalStringArray(),
        curator:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.contributor_policy?.curator)
            ? requiredStringArray(
                "At least one curator is required",
                "Curators are required"
              )
            : optionalStringArray(),
        reporter:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.contributor_policy?.reporter)
            ? requiredStringArray(
                "At least one reporter is required",
                "Reporters are required"
              )
            : optionalStringArray(),
        author:
          isActive &&
          isFieldMandatoryStrict(selectedProduct?.contributor_policy?.author)
            ? requiredStringArray(
                "At least one author is required",
                "Authors are required"
              )
            : optionalStringArray(),
        landscapeThumbId:
          isActive && mediaRatio === "landscape" && assetType !== "audio"
            ? Yup.string().required("This field is required")
            : Yup.string(),
        portraitThumbId:
          isActive && mediaRatio === "portrait" && assetType !== "audio"
            ? Yup.string().required("This field is required")
            : Yup.string(),
        premiereThumbId:
          isActive && mediaRatio === "premiere" && assetType !== "audio"
            ? Yup.string().required("This field is required")
            : Yup.string(),
        podcastThumbId:
          isActive && mediaRatio === "podcast" && assetType !== "audio"
            ? Yup.string().required("This field is required")
            : Yup.string(),
        premiereVThumbId: Yup.string(),
        gcl:
          isActive && isFieldMandatoryStrict(selectedProduct?.geo_policy?.gcl)
            ? requiredStringArray(
                "At least one country is required",
                "Countries are required"
              )
            : optionalStringArray(),
        gct:
          isActive && isFieldMandatoryStrict(selectedProduct?.geo_policy?.gct)
            ? Yup.number().required("This field is required")
            : Yup.number(),
      });
    };
  }, [
    getMediaState,
    isContentType2,
    isDefaultHost,
    mediaDetailConfig,
    mediaRatio,
    selectedProduct,
  ]);

  return validationSchema;
};
